/**
 * FinancialTimelines.jsx — Wave F3-D list page.
 *
 * Browse the most-recent Financial Lifecycle IDs with health signal,
 * last-stage badge, and quick actions (open timeline, jump to entity).
 * Powered by `GET /api/financial-timelines` and `GET /api/financial-
 * timeline/{fl_id}/summary`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, Search, ShieldCheck, ShieldAlert, ArrowRight,
  CheckCircle2, Clock, XCircle, RotateCcw,
} from 'lucide-react';


const STAGE_LABEL = {
  document:       'المُستَنَد',
  approval:       'الاعتِماد',
  bill:           'الفاتورَة',
  journal:        'القَيد',
  payment:        'الدَّفع',
  reconciliation: 'المُطابَقَة',
};

const STAGE_COLOR = {
  document:       'bg-slate-100 text-slate-800',
  approval:       'bg-amber-100 text-amber-800',
  bill:           'bg-indigo-100 text-indigo-800',
  journal:        'bg-blue-100 text-blue-800',
  payment:        'bg-emerald-100 text-emerald-800',
  reconciliation: 'bg-teal-100 text-teal-800',
};

function healthBand(score) {
  if (score >= 90) return { label: 'ممتاز', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
  if (score >= 70) return { label: 'جَيد',  color: 'text-blue-600 bg-blue-50 border-blue-200' };
  if (score >= 40) return { label: 'مُتَوَسِط', color: 'text-amber-600 bg-amber-50 border-amber-200' };
  return { label: 'ضَعيف', color: 'text-red-600 bg-red-50 border-red-200' };
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ar-SA', {
      hour12: false, year: 'numeric', month: 'short',
      day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16); }
}


export default function FinancialTimelines() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/financial-timelines', {
        params: { limit: 100 },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل تَحميل القائِمَة');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => (it.fl_id || '').toLowerCase().includes(s));
  }, [items, q]);

  const stats = useMemo(() => ({
    total:       items.length,
    reconciled:  items.filter((i) => i.has_reconciled).length,
    reversed:    items.filter((i) => i.has_reversed).length,
    healthy:     items.filter((i) => (i.health_lite || 0) >= 80).length,
  }), [items]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="fintl-list-root">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 text-slate-900">
            <Activity className="w-8 h-8 text-blue-600" />
            الخَط الزَّمَني المالي
          </h1>
          <p className="text-slate-600 mt-1">
            رِحلَة كُل مُستَنَد مالي من الرَّفع حَتى المُطابَقَة البَنكيَّة —
            مَع مُدَد المَراحِل، مُؤَشِّرات SLA، وَنَتيجَة سَلامَة (Health Score).
          </p>
        </div>
        <button
          data-testid="fintl-refresh-btn"
          onClick={load}
          className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-50 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> تَحديث
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="عَدَد الدَّورات" value={stats.total}
                  icon={Activity} color="text-blue-600" testid="fintl-stat-total" />
        <StatCard label="سَلامَة عاليَة" value={stats.healthy}
                  icon={ShieldCheck} color="text-emerald-600"
                  testid="fintl-stat-healthy" />
        <StatCard label="مُطابَقَة بَنكيَّة" value={stats.reconciled}
                  icon={CheckCircle2} color="text-teal-600"
                  testid="fintl-stat-reconciled" />
        <StatCard label="قُيود مَعكوسَة" value={stats.reversed}
                  icon={RotateCcw} color="text-amber-600"
                  testid="fintl-stat-reversed" />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute right-3 top-3 w-4 h-4 text-slate-400" />
        <input
          data-testid="fintl-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="بَحث بِـ FL-ID (مِثال: FL-2026-000123)"
          className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            جاري التَّحميل…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            لا تُوجَد دَورات ماليَّة لِلعَرض بَعد.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-700 text-sm">
              <tr>
                <th className="px-4 py-3 text-right">FL-ID</th>
                <th className="px-4 py-3 text-right">الحالَة الأَخيرَة</th>
                <th className="px-4 py-3 text-right">عَدَد الأَحداث</th>
                <th className="px-4 py-3 text-right">أَوَّل حَدَث</th>
                <th className="px-4 py-3 text-right">آخِر تَحديث</th>
                <th className="px-4 py-3 text-right">Health</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filtered.map((it) => {
                const band = healthBand(it.health_lite);
                return (
                  <tr key={it.fl_id}
                      data-testid={`fintl-row-${it.fl_id}`}
                      className="border-t hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-blue-700">
                      {it.fl_id}
                      {it.has_reversed && (
                        <span className="mr-2 inline-flex items-center gap-1
                                          text-xs bg-amber-100 text-amber-700
                                          rounded px-2 py-0.5">
                          <RotateCcw className="w-3 h-3" /> مَعكوس
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${STAGE_COLOR[it.last_stage] || 'bg-slate-100'}`}>
                        {STAGE_LABEL[it.last_stage] || it.last_stage}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{it.event_count}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(it.first_event_at)}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(it.last_event_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs
                                          px-2 py-1 rounded border ${band.color}`}>
                        <ShieldCheck className="w-3 h-3" />
                        {it.health_lite}% · {band.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/financial-timeline/${it.fl_id}`}
                        data-testid={`fintl-open-${it.fl_id}`}
                        className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                      >
                        فَتح <ArrowRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-white border rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500 mb-1">{label}</div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <Icon className={`w-8 h-8 ${color}`} />
      </div>
    </div>
  );
}
